import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { message } from 'antd';
import type { ReactNode } from 'react';

import { useDebounceValue } from '@hooks/shared/useDebounceValue';
import { UseModalVisibilityHookReturn } from '@hooks/shared/useModalVisibility';
import { JobPostStatusType } from '@lib/utils/constants/applicationConstants';
import {
  JobsBoardModalFilters,
  JobsBoardModalFiltersWithSet,
  JobsBoardModalFiltersWithToggle,
  useJobsBoardFiltersViewContext,
} from '@views/jobsBoard/context';

type Props = {
  children: ReactNode;
  jobsFiltersModalVisibility: UseModalVisibilityHookReturn;
};

type JobsBoardFiltersModalContextType = {
  toggleRoleType: (roleType: string) => void;
  toggleUnionStatus: (unionStatus: string) => void;
  toggleApplicationStatus: (applicationStatus: JobPostStatusType) => void;
  setAgeRange: (value: number | null | undefined, key: 'min' | 'max') => void;
  setGenders: (genders: string[]) => void;
  setEthnicities: (ethnicities: string[]) => void;
  modalFilters: JobsBoardModalFilters;
  resetAllFilters: () => void;
  isModalFiltersEmpty: boolean;
  onApplyFilters: () => void;
  setWorkDates: (workDates: string[]) => void;

  projectLocationSearchTerm: string;
  debouncedProjectLocationSearchTerm: string;
  onClearProjectLocationSearchTerm: () => void;
  setProjectLocationSearchTerm: ReactStateUpdater<string>;
  isResetAllButtonDisabled: boolean;
  setCityStateStringList: (cityStateStringList: string[]) => void;
  setModalFilters: ReactStateUpdater<JobsBoardModalFilters>;
};

const JobsBoardFiltersModalContext = createContext(
  {} as JobsBoardFiltersModalContextType
);

export const JobsBoardFiltersModalContextProvider = ({
  children,
  jobsFiltersModalVisibility,
}: Props): JSX.Element => {
  const { setJobsBoardSearchFilters, jobsBoardSearchFilters } =
    useJobsBoardFiltersViewContext();
  const [modalFilters, setModalFilters] = useState<JobsBoardModalFilters>(
    jobsBoardSearchFilters
  );
  const [projectLocationSearchTerm, setProjectLocationSearchTerm] =
    useState<string | undefined>('');
  const [
    debouncedProjectLocationSearchTerm,
    setDebouncedProjectLocationSearchTerm,
  ] = useDebounceValue(projectLocationSearchTerm, 500);

  const onClearProjectLocationSearchTerm = useCallback(() => {
    setProjectLocationSearchTerm('');
    setDebouncedProjectLocationSearchTerm('');
  }, [setProjectLocationSearchTerm, setDebouncedProjectLocationSearchTerm]);

  useEffect(() => {
    setModalFilters(jobsBoardSearchFilters);
  }, [jobsBoardSearchFilters]);

  const isModalFiltersEmpty = useMemo(() => {
    return !Object.keys(modalFilters || {})?.length;
  }, [modalFilters]);

  const isResetAllButtonDisabled = useMemo(() => {
    return !projectLocationSearchTerm && isModalFiltersEmpty;
  }, [projectLocationSearchTerm, isModalFiltersEmpty]);

  const toggleRoleType = useMemo(
    () => buildToggleFunction('roleTypes', setModalFilters),
    [setModalFilters]
  );

  const toggleUnionStatus = useMemo(
    () => buildToggleFunction('unionStatuses', setModalFilters),
    [setModalFilters]
  );

  const toggleApplicationStatus = useMemo(
    () => buildToggleFunction('applicationStatuses', setModalFilters),
    [setModalFilters]
  );

  const setAgeRange = useCallback(
    (value: number | null | undefined, key: 'min' | 'max') => {
      setModalFilters((prev) => {
        const { ageRange: currentAgeRange, ...rest } = prev ?? {};
        const newAgeRange = { ...(currentAgeRange || {}) };

        if (!Number.isInteger(value)) {
          delete newAgeRange[key];
        } else {
          newAgeRange[key] = value;
        }

        if (!Object.keys(newAgeRange || {})?.length) {
          return rest;
        }

        return {
          ...rest,
          ageRange: newAgeRange,
        };
      });
    },
    []
  );

  const setGenders = useMemo(
    () => buildSetFunction('genders', setModalFilters),
    [setModalFilters]
  );

  const setWorkDates = useMemo(
    () => buildSetFunction('workDates', setModalFilters),
    [setModalFilters]
  );

  const setEthnicities = useMemo(
    () => buildSetFunction('ethnicities', setModalFilters),
    [setModalFilters]
  );

  const setCityStateStringList = useMemo(
    () => buildSetFunction('cityStateStringList', setModalFilters),
    [setModalFilters]
  );

  const resetAllFilters = useCallback(() => {
    setModalFilters({});
    onClearProjectLocationSearchTerm();
  }, [setModalFilters, onClearProjectLocationSearchTerm]);

  const onApplyFilters = useCallback(() => {
    if (
      Number.isInteger(modalFilters?.ageRange?.min) &&
      Number.isInteger(modalFilters?.ageRange?.max) &&
      modalFilters?.ageRange?.min > modalFilters?.ageRange?.max
    ) {
      message.error('Min age cannot be greater than max age');
      return;
    }

    setJobsBoardSearchFilters(modalFilters);
    jobsFiltersModalVisibility.hideModal();
  }, [modalFilters, setJobsBoardSearchFilters, jobsFiltersModalVisibility]);

  return (
    <JobsBoardFiltersModalContext.Provider
      value={{
        toggleRoleType,

        toggleUnionStatus,

        toggleApplicationStatus,

        setAgeRange,

        setGenders,

        setEthnicities,
        modalFilters,
        isModalFiltersEmpty,
        resetAllFilters,
        onApplyFilters,
        setWorkDates,

        projectLocationSearchTerm,
        debouncedProjectLocationSearchTerm,
        onClearProjectLocationSearchTerm,
        setProjectLocationSearchTerm,
        isResetAllButtonDisabled,
        setCityStateStringList,
        setModalFilters,
      }}
    >
      {children}
    </JobsBoardFiltersModalContext.Provider>
  );
};

export const useJobsBoardFiltersModalContext: () => JobsBoardFiltersModalContextType =
  () =>
    useContext<JobsBoardFiltersModalContextType>(JobsBoardFiltersModalContext);

const buildToggleFunction = (
  key: keyof JobsBoardModalFiltersWithToggle,
  setModalFilters: ReactStateUpdater<JobsBoardModalFilters>
) => {
  return (value: string) => {
    setModalFilters((prev) => {
      const { [key]: rawPrevValue, ...rest } = prev ?? {};

      const prevValue = rawPrevValue ?? [];

      const newValue = prevValue.includes(value)
        ? prevValue.filter((type) => type !== value)
        : [...prevValue, value];

      if (!newValue?.length) {
        return rest;
      }

      return {
        ...rest,
        [key]: newValue,
      };
    });
  };
};

const buildSetFunction = (
  key: keyof JobsBoardModalFiltersWithSet,
  setModalFilters: ReactStateUpdater<JobsBoardModalFilters>
) => {
  return (value: (string | number)[]) => {
    setModalFilters((prev) => {
      const { [key]: _, ...rest } = prev ?? {};

      if (!value?.length) {
        return rest;
      }

      return { ...rest, [key]: value };
    });
  };
};
